import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { CognitoService } from 'app/core/http/services/cognito.service';
import { User } from 'app/core/interfaces/user';

@Component({
  selector: 'src-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
})
export class RegisterComponent implements OnInit {
  public loading = false;
  public submitted = false;
  public successLogin = false;
  public form!: FormGroup;
  public user: User = {} as User;
  public errorMessage: string = '';
  public hide: Boolean = true;

  constructor(private fb: FormBuilder, private router: Router, private cognitoService: CognitoService, private snackBar: MatSnackBar) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  validatePassword(control: AbstractControl): { [key: string]: boolean } | null {
    const password = control.value;
    const hasNumber = /[0-9]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasSpecial = /[^\w\s]/.test(password);

    if (!hasNumber || !hasUpper || !hasLower || !hasSpecial) {
      return { invalidPassword: true };
    }

    return null;
  }

  ngOnInit(): void {
    this.form = this.fb.group(
      {
        email: new FormControl(null, [Validators.required, Validators.email]),
        password: new FormControl(null, [Validators.required, this.validatePassword]),
        password_confirmation: new FormControl(null, [Validators.required]),
        type: new FormControl(null, [Validators.required]),
      },
      { validators: this.matchPassword }
    );
  }

  confirmLogin() {
    this.router.navigateByUrl('cadastros');
  }

  recuperarSenha() {
    this.router.navigateByUrl('recuperar-senha');
  }

  redirectTo(uri: string) {
    this.router.navigateByUrl(uri).then(() => window.location.reload());
  }

  register() {
    if (this.form.invalid) {
      this.submitted = true;
      this.successLogin = false;
      return;
    }

    const data = this.form.value;

    this.cognitoService
      .signUp(data)
      .then(() => {
        this.submitted = true;
        this.successLogin = true;

        this.router.navigate(['confirmar-conta'], {
          queryParams: { email: data.email },
        });
      })
      .catch((error) => {

        this.submitted = true;
        this.successLogin = false;

        this.openSnackBar(this.getFriendlyErrorMessage(error), "error");

        this.errorMessage = this.getFriendlyErrorMessage(error);
      });
  }

  private getFriendlyErrorMessage(error: any): string {
    if (error.name) {
      switch (error.name) {
        case 'UsernameExistsException':
          return 'Este e-mail já está cadastrado. Tente recuperar sua senha.';

        case 'InvalidParameterException':
          return 'Formato de e-mail ou senha inválido.';

        case 'InvalidPasswordException':
          return 'A senha não atende aos requisitos mínimos.';

        case 'UserNotConfirmedException':
          return 'Sua conta ainda não foi confirmada. Verifique seu e-mail.';

        case 'CodeMismatchException':
          return 'O código de verificação informado está incorreto.';

        case 'ExpiredCodeException':
          return 'O código de verificação expirou. Solicite um novo.';

        case 'LimitExceededException':
          return 'Muitas tentativas realizadas. Tente novamente mais tarde.';

        case 'TooManyRequestsException':
          return 'Muitas requisições ao mesmo tempo. Aguarde alguns minutos.';

        case 'NotAuthorizedException':
          return 'Não foi possível realizar o cadastro. Verifique os dados informados.';

        case 'ResourceNotFoundException':
          return 'Serviço de autenticação indisponível no momento.';

        case 'InternalErrorException':
          return 'Ocorreu um erro interno. Tente novamente mais tarde.';

        case 'InvalidLambdaResponseException':
          return 'Erro ao processar o cadastro. Contate o suporte.';

        case 'PreconditionNotMetException':
          return 'Não foi possível concluir o cadastro. Verifique os requisitos.';
      }
    }

    if (error.message) {
      return error.message;
    }

    return 'Ocorreu um erro durante o cadastro. Por favor, tente novamente.';
  }

  private matchPassword(AC: AbstractControl) {
    const password = AC.get('password')?.value;
    const confirmPassword = AC.get('password_confirmation')?.value;

    if (password !== confirmPassword) {
      AC.get('password_confirmation')?.setErrors({ mismatch: true });
      return { mismatch: true };
    }

    return null;
  }



  async loginWithGoogle() {
    try {
      await this.cognitoService.googleLogin();
    } catch (error) {
      console.error(error);
      this.submitted = true;
      this.successLogin = false;
    } finally {
      this.loading = false;
    }
  }

  openSnackBar(message: string, status: 'success' | 'error' | 'warning') {
    this.snackBar.open(message, '', {
      duration: 5000,
      panelClass: status === 'success' ? 'snack-success' : status === 'error' ? 'snack-error' : 'snack-warning',
      horizontalPosition: 'right',
      verticalPosition: 'top',
    });
  }

  loginWithFacebook() {
    this.openSnackBar('Login com o facebook ainda não está disponível. Utilize as outras formas de login!', 'warning');
  }

  loginWithApple() {
    this.openSnackBar('Login com o apple ainda não está disponível. Utilize as outras formas de login!', 'warning');
  }

  goToUrl(page: string) {
    this.router.navigate([page]);
  }
}
